// Enhanced Activity Timing Tracker with debugging capabilities
export interface IActivityTiming {
  activityName: string;
  activityStartTime: string;
  activityEndTime: string;
  totalDurationMS: number;
}

// Interface for tracking active (ongoing) activities
interface IActiveActivity {
  activityName: string;
  startTime: string;
}

export class ActivityTimingTracker {
  private static timings: IActivityTiming[] = [];
  private static activeActivities: IActiveActivity[] = [];

  public static startActivity(activityName: string): string {
    if (!activityName || activityName.trim() === '') {
      console.error('❌ ActivityTimingTracker.startActivity: activityName cannot be empty');
      return new Date().toISOString();
    }
    
    const startTime = new Date().toISOString();
    
    // Track this as an active activity
    this.activeActivities.push({ activityName, startTime });
    
    console.log(`⏱️ Started: ${activityName} at ${startTime}`);
    console.log(`📊 Current timing records before start: ${this.timings.length}`);
    console.log(`🔄 Active activities: ${this.activeActivities.length}`);
    return startTime;
  }

  public static endActivity(activityName: string, startTime: string): IActivityTiming {
    const endTime = new Date().toISOString();
    const startTimestamp = new Date(startTime).getTime();
    const endTimestamp = new Date(endTime).getTime();
    const durationMs = endTimestamp - startTimestamp;

    const timing: IActivityTiming = {
      activityName,
      activityStartTime: startTime,
      activityEndTime: endTime,
      totalDurationMS: durationMs
    };

    this.timings.push(timing);
    
    // Remove from active activities
    this.activeActivities = this.activeActivities.filter(
      active => !(active.activityName === activityName && active.startTime === startTime)
    );
    
    console.log(`⏱️ Completed: ${activityName} in ${durationMs}ms`);
    console.log(`📊 Timing Details:`, timing);
    console.log(`📊 Total timing records after completion: ${this.timings.length}`);
    console.log(`🔄 Remaining active activities: ${this.activeActivities.length}`);
    console.log(`📊 All activities captured so far:`, this.timings.map(t => t.activityName));

    return timing;
  }

  // New method to force-end all active activities before quote submission
  public static endAllActiveActivities(): IActivityTiming[] {
    const endedActivities: IActivityTiming[] = [];
    
    console.log(`🔚 Force-ending ${this.activeActivities.length} active activities before quote submission`);
    
    // Create a copy of the active activities array to avoid modification during iteration
    const activitiesToEnd = [...this.activeActivities];
    
    activitiesToEnd.forEach(active => {
      const timing = this.endActivity(active.activityName, active.startTime);
      endedActivities.push(timing);
      console.log(`🔚 Force-ended: ${active.activityName}`);
    });
    
    console.log(`✅ Successfully force-ended ${endedActivities.length} active activities`);
    return endedActivities;
  }

  // Method to get all active (ongoing) activities
  public static getActiveActivities(): IActiveActivity[] {
    return [...this.activeActivities];
  }

  public static getAllTimings(): IActivityTiming[] {
    console.log(`🔍 getAllTimings() called - returning ${this.timings.length} completed timing records and ${this.activeActivities.length} active activities`);
    console.log(`🔍 Completed activities in timings:`, this.timings.map(t => `${t.activityName} (${t.totalDurationMS}ms)`));
    console.log(`🔍 Active activities still running:`, this.activeActivities.map(a => `${a.activityName} (started at ${a.startTime})`));
    
    // Calculate total duration for logging
    const totalDuration = this.timings.reduce((sum, timing) => sum + timing.totalDurationMS, 0);
    console.log(`🔍 Total duration of all completed activities: ${totalDuration}ms`);
    
    return [...this.timings];
  }

  public static getTimingsByActivity(activityName: string): IActivityTiming[] {
    return this.timings.filter(timing => timing.activityName === activityName);
  }

  public static clearTimings(): void {
    console.log(`🗑️ clearTimings() called - clearing ${this.timings.length} timing records and ${this.activeActivities.length} active activities`);
    console.log(`🗑️ Before clear - Completed activities:`, this.timings.map(t => `${t.activityName} (${t.totalDurationMS}ms)`));
    console.log(`🗑️ Before clear - Active activities:`, this.activeActivities.map(a => a.activityName));
    this.timings = [];
    this.activeActivities = [];
    console.log(`✅ Cleared all timing data - fresh start for new session`);
  }

  public static logTimingSummary(): void {
    console.log('📈 Activity Timing Summary:');
    console.log(`📈 Total completed activities: ${this.timings.length}`);
    console.log(`🔄 Total active activities: ${this.activeActivities.length}`);
    
    this.timings.forEach((timing, index) => {
      console.log(`  ${index + 1}. ${timing.activityName}: ${timing.totalDurationMS}ms`);
    });
    
    if (this.activeActivities.length > 0) {
      console.log('🔄 Active (ongoing) activities:');
      this.activeActivities.forEach((active, index) => {
        const timeElapsed = new Date().getTime() - new Date(active.startTime).getTime();
        console.log(`  ${index + 1}. ${active.activityName}: ${timeElapsed}ms (still running)`);
      });
    }
    
    // Group by activity name
    const grouped = this.timings.reduce((acc, timing) => {
      if (!acc[timing.activityName]) {
        acc[timing.activityName] = [];
      }
      acc[timing.activityName].push(timing);
      return acc;
    }, {} as Record<string, IActivityTiming[]>);
    
    console.log('📈 Grouped by Activity:');
    Object.entries(grouped).forEach(([activity, timingList]) => {
      console.log(`  ${activity}: ${timingList.length} executions`);
    });
  }

  // New method to check what's currently stored
  public static debugCurrentState(): void {
    console.log(`🔍 DEBUG: Current ActivityTimingTracker state:`);
    console.log(`🔍 Number of completed timing records: ${this.timings.length}`);
    console.log(`🔍 Number of active activities: ${this.activeActivities.length}`);
    console.log(`🔍 All completed timing records:`, this.timings);
    console.log(`🔍 All active activities:`, this.activeActivities);
    console.log(`🔍 Completed activity names:`, this.timings.map(t => t.activityName));
    console.log(`🔍 Active activity names:`, this.activeActivities.map(a => a.activityName));
  }

  // Method to manually populate all activities for testing
  public static populateAllActivitiesForTesting(): IActivityTiming[] {
    console.log(`🧪 Populating all activities for testing...`);
    
    const allActivities = [
      'ProductSkuAvailability', 'BundleSelection', 'BundleDetailsRetrieval', 
      'InventoryCheck', 'InventoryListUpdate', 'InventoryByQuoteUpdate', 
      'ProductSearch', 'AddCustomerContact', 'AddCustomerAccount'
    ];
    
    // Clear existing timings first
    this.clearTimings();
    
    allActivities.forEach((activity, index) => {
      const baseTime = Date.now() - (1000 * (allActivities.length - index));
      const startTime = new Date(baseTime).toISOString();
      const endTime = new Date(baseTime + 150 + (index * 50)).toISOString();
      
      const timing: IActivityTiming = {
        activityName: activity,
        activityStartTime: startTime,
        activityEndTime: endTime,
        totalDurationMS: 150 + (index * 50) // Keep in milliseconds
      };
      
      this.timings.push(timing);
      console.log(`✅ Added test timing for: ${activity} (${timing.totalDurationMS}ms)`);
    });
    
    console.log(`🧪 Test population complete! Total activities: ${this.timings.length}`);
    this.debugCurrentState();
    return this.timings;
  }
}

// Global instance for easy access
export const globalActivityTracker = ActivityTimingTracker;

// Make it available globally for browser console debugging
(window as any).ActivityTimingTracker = ActivityTimingTracker;
(window as any).debugTimings = () => {
  console.log("🔍 MANUAL DEBUG - Current timing state:");
  ActivityTimingTracker.debugCurrentState();
  ActivityTimingTracker.logTimingSummary();
  return {
    completed: ActivityTimingTracker.getAllTimings(),
    active: ActivityTimingTracker.getActiveActivities()
  };
};
(window as any).endAllActiveTimings = () => {
  console.log("🔚 MANUAL END - Force ending all active timings:");
  const endedActivities = ActivityTimingTracker.endAllActiveActivities();
  console.log("🔚 Ended activities:", endedActivities);
  return endedActivities;
};
(window as any).populateAllActivities = () => {
  console.log("🧪 MANUAL TEST - Populating all activities:");
  return ActivityTimingTracker.populateAllActivitiesForTesting();
};

// React hook for easy integration
export const useActivityTiming = () => {
  return {
    startActivity: ActivityTimingTracker.startActivity,
    endActivity: ActivityTimingTracker.endActivity,
    getAllTimings: ActivityTimingTracker.getAllTimings,
    getActiveActivities: ActivityTimingTracker.getActiveActivities,
    endAllActiveActivities: ActivityTimingTracker.endAllActiveActivities,
    clearTimings: ActivityTimingTracker.clearTimings,
    logTimingSummary: ActivityTimingTracker.logTimingSummary,
    debugCurrentState: ActivityTimingTracker.debugCurrentState
  };
};
